import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { App } from '@capacitor/app';

import { environment } from 'src/environments/environment';
import { SecurityService } from '../shared/security/security.service';
import { StorageService } from '../shared/storage/storage.service';

/**
 * Mede o tempo de uso do app do agente: uma sessão vai da abertura/entrada em
 * foreground até o background ou o logout. Cada sessão fechada é reportada ao
 * agent-api, que soma ("Tempo ativo") e guarda a última abertura ("Último
 * acesso") exibidos no painel.
 *
 * Robustez:
 *  - o início da sessão é persistido em storage; se o app for morto sem evento
 *    de background, a sessão pendente é recuperada na próxima abertura (com um
 *    teto p/ não inflar quando o app ficou dias fechado);
 *  - o backend ainda capa a duração por sessão como blindagem final.
 */
@Injectable({ providedIn: 'root' })
export class AppUsageService {
  private static readonly START_KEY = 'usage_session_start';
  /** Teto ao recuperar uma sessão órfã (app morto): assume uso curto antes do crash. */
  private static readonly RECOVERY_CAP_MS = 15 * 60 * 1000;

  private initialized = false;
  private pendingStart: string | null = null;

  constructor(
    private http: HttpClient,
    private securityService: SecurityService,
    private storageService: StorageService
  ) {}

  /** Inicia o rastreio. Deve ser chamado após a sessão de segurança carregar. */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    // 1) Recupera uma sessão órfã (app morto sem evento de background).
    this.recoverPendingSession();

    // 2) Se já está logado (cold start com sessão), abre uma sessão.
    if (this.securityService.valid) {
      this.startSession();
    }

    // 3) Foreground/background.
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        if (this.securityService.valid) {
          this.startSession();
        }
      } else {
        this.flush(new Date());
      }
    });

    // 4) Logout encerra a sessão corrente.
    this.securityService.onInvalidate.subscribe(() => this.flush(new Date()));
  }

  /** Marca o início de uma sessão (se ainda não houver uma em andamento). */
  startSession(): void {
    if (this.pendingStart) {
      return;
    }
    this.pendingStart = this.toLocalIso(new Date());
    this.storageService.set(AppUsageService.START_KEY, this.pendingStart).catch(() => {});
  }

  /** Fecha a sessão corrente e reporta ao backend. */
  private flush(endedAt: Date): void {
    const startedAt = this.pendingStart;
    this.pendingStart = null;
    this.storageService.remove(AppUsageService.START_KEY).catch(() => {});
    if (!startedAt) {
      return;
    }
    this.report(startedAt, this.toLocalIso(endedAt));
  }

  private recoverPendingSession(): void {
    this.storageService.get(AppUsageService.START_KEY).then((startedAt: string | null) => {
      if (!startedAt) {
        return;
      }
      this.storageService.remove(AppUsageService.START_KEY).catch(() => {});
      const startMs = new Date(startedAt).getTime();
      if (Number.isNaN(startMs)) {
        return;
      }
      // App morto sem fechar a sessão: assume um uso curto (teto), não o tempo
      // real até reabrir (que poderia ser dias).
      const endedMs = Math.min(Date.now(), startMs + AppUsageService.RECOVERY_CAP_MS);
      this.report(startedAt, this.toLocalIso(new Date(endedMs)));
    }).catch(() => {});
  }

  private report(startedAt: string, endedAt: string): void {
    if (!this.securityService.valid) {
      return;
    }
    this.http.post(`${environment.agent.url}/agent/app/usage-session`, { startedAt, endedAt })
      .subscribe({ next: () => {}, error: () => {} });
  }

  /** ISO local sem timezone (o backend interpreta como LocalDateTime). */
  private toLocalIso(date: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
      + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }
}
